import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { UnlockModalErrors } from '@modals/UnlockJobModal/components/errors';
import RefreshBalance from '@components/icons/RefreshBalance.svg';
import { Content } from '@modals/UnlockJobModal/styles/Content';
import { TProps } from '@modals/UnlockJobModal/types/TProps';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { convertDuration } from '@helpers/convertDuration';
import { resolveOrganizationBillingAccountId } from '@helpers/resolveOrganizationBillingAccountId';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useWebsocketContext } from '@providers/WebsocketProvider';
import { BillingService, type GetManyForUserResponseDto, JobsService, LayoutsService, PaymentService } from 'app/API';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@components/core-ui/form-elements/buttons/Button';

export function UnlockJobModalFull({ onCancel, onSuccess, id, name }: TProps) {
  const { updateMe, organization, workspace } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const { call } = useAPI();

  const [price, setPrice] = useState<{
    duration?: number;
    alreadyPaid?: number;
    totalJobCost?: number;
    amount?: number;
  }>({ duration: 0, alreadyPaid: 0, totalJobCost: 0, amount: 0 });
  const [paymentMethodId, setPaymentMethodId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [balanceIsRefreshing, setBalanceIsRefreshing] = useState(false);
  const [selectedCloudLayout, setSelectedCloudLayout] = useState('null');
  const [layouts, setLayouts] = useState<GetManyForUserResponseDto[] | undefined>([]);
  const { onWSUpdate } = useWebsocketContext();

  const layoutsList = [
    {
      label: 'None',
      value: 'null',
    },
    ...(layouts || []).map((l) => ({
      value: l.id,
      label: l.name,
    })),
  ];

  const balance = organization?.billingAccount?.currentBalance ?? 0;
  const notEnoughFunds = (price?.amount ?? 0) > balance;
  const noBillingAccount = !resolveOrganizationBillingAccountId(organization);

  // todo: this looks suspicious. Runs on every WS update?
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (!workspace || !organization) {
        return;
      }
      const meData = await updateMe();
      const org = meData?.organizations?.find((item) => item.id === organization.id) ?? organization;
      const baId = resolveOrganizationBillingAccountId(org);
      if (!baId) {
        setIsLoading(false);
        return;
      }
      const methods = await call(PaymentService.billingAccountPaymentMethodListForJobSubmission({ baId }));
      setPaymentMethodId(methods?.find((item) => item.default)?.id);
      const priceData = await call(BillingService.calculateJobUnlockPricing({ jobId: id }));
      if (priceData?.result) {
        setPrice(priceData?.result);
      }
      const jobData = await call(JobsService.getJobById({ jobId: id, wsid: workspace?.id ?? '' }));
      setSelectedCloudLayout(jobData?.layoutId || 'null');

      const layouts = await call(LayoutsService.getOrganizationLayouts({ oid: organization?.id ?? '' }));
      setLayouts(layouts);
      setIsLoading(false);
    })();
  }, [onWSUpdate]);

  const error = noBillingAccount || (!paymentMethodId && notEnoughFunds) || notEnoughFunds;

  const submit = async () => {
    setIsLoading(true);
    await call(
      JobsService.updateLayout({
        jobId: id,
        wsid: workspace?.id ?? '',
        requestBody: { layoutId: selectedCloudLayout === 'null' ? null : selectedCloudLayout },
      }),
    );
    await call(BillingService.unlockJob({ wsid: workspace?.id ?? '', jobId: id }), {
      onError: (message) => toast.error(message),
    });
    openModal(ModalNamesEnum.ChooseCloudLayout, {
      id,
      name,
      onSuccess,
      onCancel: closeModal,
    });
    onSuccess();
  };

  return (
    <ModalWrapper width="460px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Unlock Job</ModalTitle>

      <Content>
        <div className="title">Job title</div>
        <div className="title-value">{name || 'Unnamed'}</div>

        <div className="title">Layout</div>
        <Select
          name="layoutId"
          className="layout-select"
          label="Layout"
          value={selectedCloudLayout}
          disabled={layoutsList.length < 2 || isLoading}
          onChange={(_, value) => setSelectedCloudLayout(value)}
          options={layoutsList}
          required={false}
        />

        <div className="title">Current balance</div>
        <div className={`title-value ${balanceIsRefreshing ? 'balance-refresh' : ''}`}>
          {formatCentsPrice(balance)}
          <RefreshBalance
            onClick={() => {
              if (balanceIsRefreshing) {
                return;
              }
              setBalanceIsRefreshing(true);

              updateMe()
                .then(() => {
                  setBalanceIsRefreshing(false);
                  setIsLoading(false);
                })
                .catch(() => {
                  setBalanceIsRefreshing(false);
                  setIsLoading(false);
                });
            }}
          />
        </div>

        <div className="price-description">
          <div>
            <div className="dflex noBorder">
              <span>Duration:</span>
              {convertDuration(price?.duration ?? 0)}
            </div>

            <div className="dflex">
              <span>Total cost:</span>
              {formatCentsPrice(price?.totalJobCost ?? 0)}
            </div>

            <div className="dflex">
              <span>Already paid:</span>
              {formatCentsPrice(price?.alreadyPaid ?? 0)}
            </div>

            <div className="dflex bold">
              <span>In order to unlock this job you will be charged:</span> {formatCentsPrice(price?.amount ?? 0)}
            </div>
          </div>
        </div>

        <div className={`errorsWrapper ${error ? 'active' : ''}`}>
          <UnlockModalErrors
            setBalanceIsRefreshing={setBalanceIsRefreshing}
            price={price}
            onCancel={onCancel}
            dataLoaded={!isLoading}
            onSuccess={onSuccess}
            jobId={id}
            jobName={name}
            isSubmitting={isLoading}
            paymentMethodId={paymentMethodId}
          />
        </div>

        <div className="buttonWrapper">
          <OutlinedButton className="w-full" onClick={onCancel}>
            Cancel
          </OutlinedButton>

          <Button className="relative w-full" disabled={isLoading || error} onClick={() => submit()}>
            Unlock
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
}
